import record from "@/assets/Bell.mp3";
import Button from "@/components/Base/Button";
import { Dialog } from "@/components/Base/Headless";
import LoadingIcon from "@/components/Base/LoadingIcon";
import Lucide from "@/components/Base/Lucide";
import { productStatus } from "@/config";
import { SocketEvent } from "@/enum/socketEvent.ts";
import { SocketResponse } from "@/pages/Chef/type.ts";
import { getOrder, TOrder } from "@/services/chefService.ts";
// import { useAppContext } from "@/use-context/Context";
import { getShop } from "@/utils/auth";
import socket from "@/utils/socket.ts";
import { showToast } from "@/utils/toast.ts";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { handleSocketEvent } from "../OrderByPOS/Component/Message/handleSocketEvent";
import { handleSocketEventCustomer } from "../OrderByPOS/Component/Message/handleSocketEventCustomer";
import { handleSocketStatusProductIn } from "../OrderByPOS/Component/Message/handleSocketStatusProductIn";
import ListItem from "./components/ListItem";
import ChefMobile from "./components/Mobile/ChefMobile";
import NotificationAlert from "./components/NotificationAlert";
import TopBar from "@/components/Themes/Rubick/TopBar";
import { TabButtonStatus } from "@/enum/tabButtonStatus";
import { apiOrderAction } from "@/services/ServiceApi";

type Event =
  | SocketEvent.WAITER_RECEIVE_PICK_UP_FOOD
  | SocketEvent.WAITER_RECEIVE_PROCESSING_FOOD
  | SocketEvent.WAITER_RECEIVE_PENDING_FOOD;

export type Status = TabButtonStatus

export interface Body {
  data: SocketBody[];
}

export interface SocketBody {
  businessId: string;
  tableId: string;
  tableName: string;
  name: string;
  nameKH: string;
  orderId: string;
  detailId: string;
}

export default function Chef() {
  const shop = getShop();
  const { t } = useTranslation();
  const sound = new Audio(record);
  // const { setAddToCart } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusRef = useRef<Status>(TabButtonStatus.PENDING);

  const [orders, setOrders] = useState<TOrder[]>([]);
  const [status, setStatus] = useState<Status>(TabButtonStatus.PENDING);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogAccept, setDialogAccept] = useState(false);
  const [dialogDelete, setDialogDelete] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dialogAcceptAll, setDialogAcceptAll] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<TOrder | null>(null);

  const [dialogAcceptPendingOrder, setDialogAcceptPendingOrder] = useState(false);
  const [dialogDeletePendingOrder, setDialogDeletePendingOrder] = useState(false);
  // const [dialogDeleteAllPendingOrder, setDialogDeleteAllPendingOrder] = useState(false);
  const [dialogAcceptAllPendingOrder, setDialogAcceptAllPendingOrder] =
    useState(false);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    const urlStatus = searchParams.get("status") as Status;
    if (urlStatus) {
      setStatus(urlStatus);
    }
  }, [searchParams]);

  const updateOrderList = (data: any[]) => {
    setOrders((prevItems) =>
      prevItems.filter(
        (prev) => !data.some((item) => item.detailId === prev._id),
      ),
    );
  };

  const handleConfirmOrder = (data: TOrder[], type: string) => {
    if (!socket.connected) {
      showToast("failed-connection");
      return;
    }

    setLoading(true);

    const body = {
      data: data.map((order) => ({
        tableId: order.tableId,
        tableName: order.tableName,
        name: order.name,
        nameKH: order.nameKH,
        orderId: order.orderId,
        detailId: order._id,
        hasAccepted: true,
      })),
    };

    socket.emit(
      SocketEvent.WAITER_SEND_CONFIRM_ORDER,
      body,
      (response: SocketResponse) => {
        if (response.success) {
          if (type === "all") {
            setOrders([]);
          } else {
            updateOrderList(body.data);
          }
          showToast("success");
        } else {
          showToast("failed-accept");
        }
        setLoading(false);
        setDialogAccept(false);
        setDialogAcceptAll(false);
      },
    );
  };

const handleConfirmPendingOrder = (items: TOrder[], type?: string) => {
  if (!socket.connected) {
    showToast("failed-connection");
    return;
  }

  setLoading(true);

  // 1. Determine which items to process based on the type
  const itemsToProcess = type === "one" ? items.slice(0, 1) : items;

  const apiData = {
    status: productStatus.Accept,
    data: itemsToProcess.map((item) => ({
      businessId: item.businessId,
      detailId: item._id,
      discount: item.discount,
      discountPercent: item.discountPercent,
      name: item.name,
      nameKH: item.nameKH,
      options: item.options,
      orderId: item.orderId,
      productStatus: item.requiresCooking ? productStatus.Accept : productStatus.Ready,
      tableId: item.tableId,
      tableName: item.tableName,
      tracking: item.tracking,
      quantity: item.quantity,
      instruction: item.instruction,
    })),
  };

  apiOrderAction(apiData)
    .then((response) => {
      console.log("response", response);

      if (response.status === 200) {
        const acceptBody = {
          data: itemsToProcess.map((item) => ({
            businessId: item.businessId,
            tableId: item.tableId,
            name: item.name,
            nameKH: item.nameKH,
            orderId: item.orderId,
            productStatus: productStatus.Accept,
            quantity: item.quantity,
          })),
        };
        console.log("acceptBody", acceptBody);

        socket.emit(
          SocketEvent.WAITER_ACCEPT_ORDER,
          acceptBody,
          async (response: any) => {
            console.log("response", response);

            if (response.success) {
              fetchOrders(statusRef.current);
              showToast("success");
            }
          }
        );
      }
    })
    .finally(() => {
      // 2. Moved setLoading(false) here so it actually waits for the API to finish
      setLoading(false);
    });

  // 3. Close the correct dialog based on the type
  if (type === "one") {
    setDialogAcceptPendingOrder(false);
  } else if (type === "all") {
    setDialogAcceptAllPendingOrder(false);
  } else {
    // Fallback just in case type is undefined
    setDialogAcceptPendingOrder(false);
    setDialogAcceptAllPendingOrder(false);
  }
};

  const handleDeleteOrderReady = (data: TOrder[]) => {
    if (!socket.connected) {
      showToast("failed-connection");
      return;
    }

    setLoading(true);

    const body = {
      status: productStatus.Reject,
      data: data.map((order) => ({
        tableId: order.tableId,
        tableName: order.tableName,
        productName: order.name,
        productNameKH: order.nameKH,
        orderId: order.orderId,
        detailId: order._id,
        hasAccepted: false,
      })),
    };

    socket.emit(
      SocketEvent.CHEF_ACTION_ORDER,
      body,
      (response: SocketResponse) => {
        if (response.success) {
          updateOrderList(body.data);
          showToast("success");
        } else {
          showToast("failed-accept");
        }
        setLoading(false);
        setDialogDelete(false);
      },
    );
  };

  const handleDeletePendingOrder = (items: TOrder[]) => {
    if (!socket.connected) {
      showToast("failed-connection");
      return;
    }

    setLoading(true);

    const rejectPendingBody = {
          status: productStatus.Reject,
          data: items.map((item) => ({
          detailId: item._id,
          productName: item.name,
          productNameKH: item.nameKH,
          orderId: item.orderId,
          tableId: item.tableId,
          tableName: item.tableName,
          })),
        };

        socket.emit(
          SocketEvent.CHEF_ACTION_ORDER,
          rejectPendingBody,
          async (response: any) => {
            // console.log("response", response);

            if (response.success) {
              fetchOrders(statusRef.current);
              showToast("success");
            }
          },
        );

        setLoading(false);
        setDialogDeletePendingOrder(false);
  }

  const fetchOrders = async (status: Status = TabButtonStatus.READY) => {
    setIsLoading(true);

    try {
      const response = await getOrder({ status });

      if (response.success) {
        setOrders(response.data ?? []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  const renderComponent = (order: TOrder) => {
    if (status === TabButtonStatus.READY) {
      return (
        <div className="flex gap-2">
          {!order.requiresCooking && (
            <Button
              className="ml-3 bg-red-500 rounded-full text-white"
              onClick={() => {
                setSelectedOrder(order);
                setDialogDelete(true);
              }}
            >
              {t("reject")}
            </Button>
          )}
          <Button
            className="bg-green-500 rounded-full text-white"
            onClick={() => {
              setSelectedOrder(order);
              setDialogAccept(true);
            }}
          >
            {t("done")}
          </Button>
        </div>
      );
    }
    if (status === TabButtonStatus.PENDING) {
      return (
        <div className="flex gap-2">
            <Button
              className="ml-3 bg-red-500 rounded-full text-white"
              onClick={() => {
                setSelectedOrder(order);
                setDialogDeletePendingOrder(true);
              }}
            >
              {t("reject")}
            </Button>
          <Button
            className="bg-green-500 rounded-full text-white"
            onClick={() => {
              setSelectedOrder(order);
              setDialogAcceptPendingOrder(true);
            }}
          >
            {t("accept")}
          </Button>
        </div>
      );
    }
    return null;
  };

  const handleSetStatus = (status: Status) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("status", status);
    setSearchParams(newParams);
    setStatus(status);
  };

  useEffect(() => {
    fetchOrders(status);
  }, [status]);

  // useEffect(() => {
  //   setAddToCart([]);
  // }, []);

  useEffect(() => {
    const handleUpdate = (data: any) => {
      if (!data) return;
      if (data.success) {
        fetchOrders(statusRef.current);
      }
    };

    const events = [
      SocketEvent.ACCEPT_ORDER,
      SocketEvent.FREE_ITEM_REQUEST,
      SocketEvent.COOK_ORDER,
      SocketEvent.DONE_ORDER,
      SocketEvent.REJECT_ORDER,
      SocketEvent.READy_ORDER,
      SocketEvent.WAITER_RECEIVE_ORDER_FROM_CUSTOMER,
    ];

    events.forEach((event) => {
      socket.on(event, handleUpdate);
    });

    return () => {
      events.forEach((event) => {
        socket.off(event, handleUpdate);
      });
    };
  }, []);

  useEffect(
    () =>
      handleSocketEvent(
        SocketEvent.WAITER_RECEIVE_PAYMENT,
        t("paymentCompletedAtTable"),
        sound,
      ),
    [],
  );
  useEffect(
    () =>
      handleSocketEventCustomer(
        SocketEvent.WAITER_SUPPORT_CUSTOMER,
        t("customerNeedsSupportAtTable"),
        sound,
      ),
    [],
  );
  useEffect(
    () =>
      handleSocketEventCustomer(
        SocketEvent.WAITER_RECEIVE_ORDER_FROM_CUSTOMER,
        t("youHaveANewOrderAtTable"),
        sound,
      ),
    [],
  );
  useEffect(
    () =>
      handleSocketEventCustomer(
        SocketEvent.FREE_ITEM_REQUEST,
        t("youHaveANewOrderAtTable"),
        sound,
      ),
    [],
  );

  useEffect(
    () => handleSocketStatusProductIn(SocketEvent.REJECT_ORDER, sound, false),
    [],
  );
  useEffect(
    () => handleSocketStatusProductIn(SocketEvent.COOK_ORDER, sound, false),
    [],
  );
  useEffect(
    () => handleSocketStatusProductIn(SocketEvent.READy_ORDER, sound, true),
    [],
  );

  // useEffect(() => {
  //   const handleAdminAction = () => {
  //     console.log("Admin action received, refreshing orders...");
  //     fetchOrders(statusRef.current);
  //   };

  //   socket.on(SocketEvent.DONE_ORDER, handleAdminAction);

  //   return () => {
  //     socket.off(SocketEvent.DONE_ORDER, handleAdminAction);
  //   };
  // }, []);

  if (!shop) {
    return null;
  }

  return (
    <div className="h-full">
      <TopBar>
        <div className="flex gap-2 md:gap-3 items-center">
          <Button
            className={`text-gray-800 font-bold text-[9px] xs:text-[11px] md:text-[14px] bg-[#F1F5F9] p-2 rounded-lg outline-none ring-0 focus:ring-0 focus:outline-none ${status === "Pending" ? "bg-blue-600 text-white" : ""} hover:cursor-pointer`}
            onClick={() => {
              (handleSetStatus(TabButtonStatus.PENDING), setIsLoading(true));
            }}
          >
            {t("customerOrder")}
          </Button>
          <Button
            className={`text-gray-800 font-bold text-[9px] xs:text-[11px] md:text-[14px] bg-[#F1F5F9] p-2 rounded-lg outline-none ring-0 focus:ring-0 focus:outline-none ${status === "Accept" ? "bg-blue-600 text-white" : ""} hover:cursor-pointer`}
            onClick={() => {
              (handleSetStatus(TabButtonStatus.ACCEPT), setIsLoading(true));
            }}
          >
            {t("requestCook")}
          </Button>
          <Button
            className={`text-gray-800 font-bold text-[9px] xs:text-[11px] md:text-[14px] bg-[#F1F5F9] p-2 rounded-lg outline-none ring-0 focus:ring-0 focus:outline-none ${status === "Cooking" ? "bg-blue-600 text-white" : ""} hover:cursor-pointer`}
            onClick={() => {
              (handleSetStatus(TabButtonStatus.COOKING), setIsLoading(true));
            }}
          >
            {t("cooking")}
          </Button>
          <Button
            className={`text-gray-800 font-bold text-[9px] xs:text-[11px] md:text-[14px] bg-[#F1F5F9] p-2 rounded-lg outline-none ring-0 focus:ring-0 focus:outline-none ${status === "Ready" ? "bg-blue-600 text-white" : ""} hover:cursor-pointer`}
            onClick={() => {
              (handleSetStatus(TabButtonStatus.READY), setIsLoading(true));
            }}
          >
            {t("cooked")}
          </Button>
        </div>
      </TopBar>

      <div className="hidden md:block">
        <div className="grid grid-cols-12 gap-4 rounded-lg mt-4 pb-[80px] h-[calc(100vh-5rem)]">
          <div className="bg-white rounded-lg p-2 col-span-12 flex flex-col h-[calc(80vh-1.2rem)] 2xl:h-[calc(100vh-10.3rem)]">
            <div className="grid grid-cols-12 mb-2 p-3 bg-primary text-white font-bold rounded-md">
              <div className="col-span-1 flex py-2 items-center">
                {t("code")}
              </div>
              <div className="col-span-2 flex py-2 items-center">
                {t("name")}
              </div>
              <div className="col-span-2 flex py-2 items-center">
                {t("orderedTime")}
              </div>
              <div className="col-span-3 pl-5 border-gray-400 grid grid-cols-4 items-center">
                <div className="col-span-1">{t("price")}</div>
                <div className="col-span-1">{t("qty")}</div>
                <div className="col-span-1 flex">{t("tableName")}</div>
              </div>
              <div className="col-span-4 flex justify-between pl-5 border-gray-400">
                <div className="flex gap-2 items-center">{t("note")}</div>
                {status === TabButtonStatus.READY && (
                  <Button
                    size="sm"
                    disabled={status === TabButtonStatus.READY && orders.length === 0}
                    className="ml-3 bg-green-500 border-none rounded-full text-white"
                    onClick={() => setDialogAcceptAll(true)}
                  >
                    {t("doneAll")}
                  </Button>
                )}
                {status === TabButtonStatus.PENDING && (
                  <Button
                    size="sm"
                    disabled={status === TabButtonStatus.PENDING && orders.length === 0}
                    className="ml-3 bg-green-500 border-none rounded-full text-white"
                    onClick={() => setDialogAcceptAllPendingOrder(true)}
                  >
                    {t("acceptAll")}
                  </Button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto">
              <ListItem
                orders={orders}
                t={t}
                isLoading={isLoading}
                renderBTN={renderComponent}
                status={status}
                shop={shop}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="block md:hidden">
        <ChefMobile
          orders={orders}
          isLoading={isLoading}
          status={status}
          shop={shop}
          setDialogAcceptAll={setDialogAcceptAll}
          setDialogAccept={setDialogAccept}
          setSelectedOrder={setSelectedOrder}
        />
      </div>

      <Dialog open={dialogAccept} onClose={() => setDialogAccept(false)}>
        <Dialog.Panel className="mt-[10vh]">
          <div className="p-5 text-center">
            <Lucide
              icon="AlertTriangle"
              className="w-16 h-16 mx-auto mt-3 text-warning"
            />
            <div className="mt-5 text-3xl">{t("areYouSure")}</div>
            <div className="mt-2 text-slate-500">
              {t("doYouWantToDoThisAction")}
              <br />
            </div>
          </div>
          <div className="px-5 pb-8 text-center">
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                setDialogAccept(false);
                setSelectedOrder(null);
              }}
              className="w-24 mr-2"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="bg-green-500 text-white w-28"
              onClick={() => {
                if (selectedOrder) {
                  handleConfirmOrder([selectedOrder], "one");
                }
              }}
              disabled={loading}
            >
              {t("confirm")}
              {loading && (
                <LoadingIcon
                  icon="oval"
                  color="white"
                  className="w-4 h-4 ml-2"
                />
              )}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>

      <Dialog open={dialogDelete} onClose={() => setDialogDelete(false)}>
        <Dialog.Panel className="mt-[10vh]">
          <div className="p-5 text-center">
            <Lucide
              icon="AlertTriangle"
              className="w-16 h-16 mx-auto mt-3 text-warning"
            />
            <div className="mt-5 text-3xl">{t("areYouSure")}</div>
            <div className="mt-2 text-slate-500">
              {t("doYouWantToDoThisAction")}
              <br />
            </div>
          </div>
          <div className="px-5 pb-8 text-center">
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                setDialogDelete(false);
                setSelectedOrder(null);
              }}
              className="w-24 mr-2"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="bg-green-500 text-white w-28"
              onClick={() => {
                if (selectedOrder) {
                  handleDeleteOrderReady([selectedOrder]);
                }
              }}
              disabled={loading}
            >
              {t("confirm")}
              {loading && (
                <LoadingIcon
                  icon="oval"
                  color="white"
                  className="w-4 h-4 ml-2"
                />
              )}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>

      <Dialog open={dialogAcceptAll} onClose={() => setDialogAcceptAll(false)}>
        <Dialog.Panel>
          <div className="p-5 text-center">
            <Lucide
              icon="AlertTriangle"
              className="w-16 h-16 mx-auto mt-3 text-warning"
            />
            <div className="mt-5 text-3xl">{t("areYouSure")}</div>
            <div className="mt-2 text-slate-500">
              {t("doYouWantToDoThisAction")}
              <br />
            </div>
          </div>
          <div className="px-5 pb-8 text-center">
            <Button
              type="button"
              variant="danger"
              onClick={() => setDialogAcceptAll(false)}
              className="w-24 mr-2"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="bg-green-500 text-white w-28"
              onClick={() => handleConfirmOrder(orders, "all")}
              disabled={loading}
            >
              {t("confirm")}
              {loading && (
                <LoadingIcon
                  icon="oval"
                  color="white"
                  className="w-4 h-4 ml-2"
                />
              )}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>

      <Dialog open={dialogAcceptPendingOrder} onClose={() => setDialogAcceptPendingOrder(false)}>
        <Dialog.Panel>
          <div className="p-5 text-center">
            <Lucide
              icon="AlertTriangle"
              className="w-16 h-16 mx-auto mt-3 text-warning"
            />
            <div className="mt-5 text-3xl">{t("areYouSure")}</div>
            <div className="mt-2 text-slate-500">
              {t("doYouWantToDoThisAction")}
              <br />
            </div>
          </div>
          <div className="px-5 pb-8 text-center">
            <Button
              type="button"
              variant="danger"
              onClick={() => setDialogAcceptPendingOrder(false)}
              className="w-24 mr-2"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="bg-green-500 text-white w-28"
              onClick={() => handleConfirmPendingOrder(orders, "one")}
              disabled={loading}
            >
              {t("confirm")}
              {loading && (
                <LoadingIcon
                  icon="oval"
                  color="white"
                  className="w-4 h-4 ml-2"
                />
              )}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>

      <Dialog open={dialogAcceptAllPendingOrder} onClose={() => setDialogAcceptAllPendingOrder(false)}>
        <Dialog.Panel>
          <div className="p-5 text-center">
            <Lucide
              icon="AlertTriangle"
              className="w-16 h-16 mx-auto mt-3 text-warning"
            />
            <div className="mt-5 text-3xl">{t("areYouSure")}</div>
            <div className="mt-2 text-slate-500">
              {t("doYouWantToDoThisAction")}
              <br />
            </div>
          </div>
          <div className="px-5 pb-8 text-center">
            <Button
              type="button"
              variant="danger"
              onClick={() => setDialogAcceptAllPendingOrder(false)}
              className="w-24 mr-2"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="bg-green-500 text-white w-28"
              onClick={() => handleConfirmPendingOrder(orders, "all")}
              disabled={loading}
            >
              {t("confirm")}
              {loading && (
                <LoadingIcon
                  icon="oval"
                  color="white"
                  className="w-4 h-4 ml-2"
                />
              )}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>

      <Dialog open={dialogDeletePendingOrder} onClose={() => setDialogDeletePendingOrder(false)}>
        <Dialog.Panel className="mt-[10vh]">
          <div className="p-5 text-center">
            <Lucide
              icon="AlertTriangle"
              className="w-16 h-16 mx-auto mt-3 text-warning"
            />
            <div className="mt-5 text-3xl">{t("areYouSure")}</div>
            <div className="mt-2 text-slate-500">
              {t("doYouWantToDoThisAction")}
              <br />
            </div>
          </div>
          <div className="px-5 pb-8 text-center">
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                setDialogDeletePendingOrder(false);
                setSelectedOrder(null);
              }}
              className="w-24 mr-2"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="bg-green-500 text-white w-28"
              onClick={() => {
                if (selectedOrder) {
                  handleDeletePendingOrder([selectedOrder]);
                }
              }}
              disabled={loading}
            >
              {t("confirm")}
              {loading && (
                <LoadingIcon
                  icon="oval"
                  color="white"
                  className="w-4 h-4 ml-2"
                />
              )}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>

      <NotificationAlert />
    </div>
  );
}
